import React, { useEffect, useState } from 'react';
import { fetchGalleries } from '../lib/cms';
import GalleryModal from '../components/GalleryModal';
import { getOptimizedImageUrl } from '../lib/utils';

export default function Gallery() {
  const [galleries, setGalleries] = useState([]);
  const [activeGalleryIndex, setActiveGalleryIndex] = useState(-1);
  const [loading, setLoading] = useState(true);

  // Lightbox state
  const [activeImageIndex, setActiveImageIndex] = useState(null);
  const [activeYouTube, setActiveYouTube] = useState({});

  const videos = [
    {
      id: 'gone-blind',
      title: 'Gone Blind (Live fra St. Croix)',
      description: 'Live-optræden fra St. Croix, US Virgin Islands med Steven Katz og Heidi Svelmøe.',
      type: 'file',
      src: 'https://assets.auxodev.dk/assets/87cec7ac-eba2-4923-aa73-844d6660fb0e/gone-blind-live-st-croix.mp4',
    },
    {
      id: 'love-as-a-battlefield',
      title: 'Love as a Battlefield',
      description: 'Officiel musikvideo med Heidi og bandet StellarPirate.',
      type: 'youtube',
      youtubeId: 'ftojCCiA-CI',
      thumbnail: 'https://i.ytimg.com/vi/ftojCCiA-CI/hqdefault.jpg',
    },
    {
      id: 'live-sneak-peek',
      title: 'Heidi & StellarPirate Live Sneak Peek',
      description: 'Live-optræden og forsmag på musikken med Heidi og StellarPirate.',
      type: 'youtube',
      youtubeId: '5-P2q252b50',
      thumbnail: 'https://assets.auxodev.dk/assets/87cec7ac-eba2-4923-aa73-844d6660fb0e/maxresdefault.webp',
    },
  ];

  useEffect(() => {
    async function loadGalleries() {
      const data = await fetchGalleries();
      setGalleries(data || []);
      setLoading(false);
    }
    loadGalleries();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[80vh] items-center justify-center bg-sand-50">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-clay-500 border-t-transparent" />
      </div>
    );
  }

  const sortedGalleries = [...galleries].sort((a, b) => {
    const getWeight = (g) => {
      const title = (g.title?.da || '').toLowerCase();
      const slug = (g.slug?.da || '').toLowerCase();
      if (title.includes('sangundervisning') || slug.includes('sangundervisning')) return 1;
      if (title.includes('audio') || slug.includes('audio')) return 2;
      if (title.includes('presse') || title.includes('press') || slug.includes('presse')) return 3;
      if (title.includes('blandede') || slug.includes('blandede')) return 4;
      return 5;
    };
    return getWeight(a) - getWeight(b);
  });

  const allImages = Array.from(new Set(galleries.flatMap((g) => g.images || [])));

  // If activeGalleryIndex === -1, show 'All'
  const isAll = activeGalleryIndex === -1;
  const activeGallery = isAll
    ? {
        title: { da: 'Alle billeder' },
        description: { da: 'Samlet oversigt over alle billeder fra sangundervisning, koncerter, turnéer og pressemateriale.' },
        images: allImages,
      }
    : sortedGalleries[activeGalleryIndex];

  const activeImages = activeGallery?.images || [];

  return (
    <div className="bg-sand-50 text-foreground min-h-screen">
      {/* Header Banner */}
      <section className="relative py-20 md:py-28 bg-[#60b6c0] text-sand-50 overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://assets.auxodev.dk/assets/87cec7ac-eba2-4923-aa73-844d6660fb0e/sean-o-406693_4.jpg?width=1920&quality=60&format=auto"
            alt="Galleri"
            className="h-full w-full object-cover object-center"
          />
        </div>
        <div className="relative z-10 mx-auto max-w-4xl px-6 text-center flex flex-col gap-4">
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold tracking-wide leading-tight">
            Billedgalleri
          </h1>
          <p className="max-w-2xl mx-auto text-sm sm:text-base text-white leading-relaxed">
            Gå på opdagelse i billederne fra sangundervisningen, koncerterne, pressebillederne og Heidis musikalske rejse.
          </p>
        </div>
      </section>

      {/* Gallery Content */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          {galleries.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-3xl border border-sand-200 p-8">
              <i className="fa-light fa-images text-4xl text-sand-400 mb-4" />
              <h3 className="font-serif text-xl font-bold text-black">Ingen gallerier fundet</h3>
              <p className="text-sm text-foreground/60 mt-2">Der er i øjeblikket ingen billedgallerier tilgængelige.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-10">
              {/* Gallery Tabs */}
              <div className="flex flex-wrap justify-center gap-2 sm:gap-3 border-b border-sand-200 pb-6">
                <button
                  onClick={() => {
                    setActiveGalleryIndex(-1);
                    setActiveImageIndex(null);
                  }}
                  className={`rounded-full px-5 py-2.5 text-xs uppercase tracking-widest font-semibold transition-all focus:outline-none flex items-center gap-2 ${
                    isAll
                      ? 'bg-cta text-clay-900 shadow-md'
                      : 'bg-white text-foreground/70 border border-sand-200 hover:bg-sand-100'
                  }`}
                >
                  <span>Alle billeder</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${isAll ? 'bg-clay-900/15 text-clay-900' : 'bg-sand-100 text-foreground/60'}`}>
                    {allImages.length}
                  </span>
                </button>

                {sortedGalleries.map((gallery, index) => {
                  const count = gallery.images?.length || 0;
                  const isActive = activeGalleryIndex === index;
                  return (
                    <button
                      key={gallery.id}
                      onClick={() => {
                        setActiveGalleryIndex(index);
                        setActiveImageIndex(null);
                      }}
                      className={`rounded-full px-5 py-2.5 text-xs uppercase tracking-widest font-semibold transition-all focus:outline-none flex items-center gap-2 ${
                        isActive
                          ? 'bg-cta text-clay-900 shadow-md'
                          : 'bg-white text-foreground/70 border border-sand-200 hover:bg-sand-100'
                      }`}
                    >
                      <span>{gallery.title.da}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${isActive ? 'bg-clay-900/15 text-clay-900' : 'bg-sand-100 text-foreground/60'}`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Gallery Description */}
              {activeGallery?.description?.da && (
                <div className="text-center max-w-2xl mx-auto -mt-2">
                  <p className="text-sm text-foreground/70 italic">
                    {activeGallery.description.da}
                  </p>
                </div>
              )}

              {/* Images Grid */}
              {activeImages.length === 0 ? (
                <div className="text-center py-12 text-foreground/50">
                  Dette galleri indeholder ingen billeder endnu.
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                  {activeImages.map((imgUrl, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveImageIndex(index)}
                      className="group relative overflow-hidden rounded-none shadow-sm aspect-square border border-sand-200 bg-white focus:outline-none"
                    >
                      <img
                        src={getOptimizedImageUrl(imgUrl, { width: 600, height: 600, fit: 'cover', quality: 80 }) || `${imgUrl}?width=400&height=400&fit=cover&format=auto`}
                        alt={`${activeGallery.title.da} - Billede ${index + 1}`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <i className="fa-light fa-magnifying-glass-plus text-white text-2xl drop-shadow-md" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      {activeImageIndex !== null && activeImages.length > 0 && (
        <GalleryModal
          images={activeImages}
          currentIndex={activeImageIndex}
          onClose={() => setActiveImageIndex(null)}
          onPrev={() => setActiveImageIndex((prev) => (prev === 0 ? activeImages.length - 1 : prev - 1))}
          onNext={() => setActiveImageIndex((prev) => (prev === activeImages.length - 1 ? 0 : prev + 1))}
        />
      )}

      {/* Videos Section */}
      <section className="py-16 md:py-24 bg-white border-t border-sand-200/80">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center max-w-3xl mx-auto mb-12 flex flex-col gap-4">
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-wide text-black">
              Videos
            </h2>
            <p className="text-base text-foreground/70 leading-relaxed">
              Live-optrædener og musikvideoer med Heidi Svelmøe og StellarPirate.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {videos.map((v) => (
              <div key={v.id} className="flex flex-col gap-3">
                <div className="overflow-hidden rounded-none shadow-md border border-sand-200 bg-black aspect-video relative">
                  {v.type === 'file' ? (
                    <video
                      src={v.src}
                      controls
                      playsInline
                      preload="none"
                      className="w-full h-full object-contain"
                    >
                      Din browser understøtter ikke videoafspilning.
                    </video>
                  ) : activeYouTube[v.id] ? (
                    <iframe
                      src={`https://www.youtube-nocookie.com/embed/${v.youtubeId}?autoplay=1&rel=0`}
                      title={v.title}
                      className="w-full h-full border-0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setActiveYouTube((prev) => ({ ...prev, [v.id]: true }))}
                      className="w-full h-full relative group block focus:outline-none"
                      aria-label={`Afspil ${v.title}`}
                    >
                      <img
                        src={v.thumbnail}
                        alt={v.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <div className="w-14 h-14 rounded-full bg-[#FF0000] text-white flex items-center justify-center shadow-lg transition-transform group-hover:scale-110">
                          <i className="fa-solid fa-play text-xl ml-1" />
                        </div>
                      </div>
                    </button>
                  )}
                </div>
                <div className="text-left">
                  <h3 className="font-serif text-lg font-bold text-black">
                    {v.title}
                  </h3>
                  <p className="text-sm text-foreground/70 mt-1">
                    {v.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
